import { useCallback, useEffect, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";

const ROLE_COLOR = { owner: "#6366f1", admin: "#f59e0b", agent: "#38bdf8" };
const ROLE_BG    = { owner: "rgba(99,102,241,0.12)", admin: "rgba(245,158,11,0.12)", agent: "rgba(56,189,248,0.12)" };
const DEFAULT_ACCESS = ["overview", "chats", "contacts", "pipeline", "schedule", "profile"];
const ADMIN_ACCESS = ["overview", "chats", "contacts", "pipeline", "broadcast", "schedule", "knowledge", "safety", "insights", "shopify", "woocommerce", "ecom_pipeline", "messenger", "instagram", "numbers", "profile"];
const ACCESS_GROUPS = [
  { title: "Workspace", items: [
    ["overview", "Overview"], ["chats", "Chats"], ["contacts", "Contacts"], ["pipeline", "Lead Gen"], ["broadcast", "Broadcast"],
    ["schedule", "Schedule"], ["knowledge", "Knowledge"], ["safety", "Safety"], ["insights", "AI Insights"],
  ]},
  { title: "E-Commerce", items: [["shopify", "Shopify"], ["woocommerce", "WooCommerce"], ["ecom_pipeline", "E-com Pipeline"]]},
  { title: "Meta", items: [["messenger", "Messenger"], ["instagram", "Instagram"]]},
  { title: "Account", items: [["numbers", "My WhatsApp"], ["profile", "Profile"]]},
];

const RoleBadge = ({ role }) => (
  <span style={{ fontSize:10, padding:"2px 8px", borderRadius:8, fontWeight:700,
    background: ROLE_BG[role]||"rgba(100,116,139,0.12)", color: ROLE_COLOR[role]||T.text3,
    textTransform:"capitalize" }}>
    {role}
  </span>
);

const Avatar = ({ name, size=36 }) => {
  const ch = (name||"?")[0].toUpperCase();
  const colors = ["#6366f1","#8b5cf6","#38bdf8","#10b981","#f59e0b"];
  const bg = colors[ch.charCodeAt(0) % colors.length];
  return (
    <div style={{ width:size,height:size,borderRadius:size*0.28,background:`${bg}22`,border:`1px solid ${bg}44`,
      display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.4,fontWeight:700,color:bg,flexShrink:0 }}>
      {ch}
    </div>
  );
};

const StatusDot = ({ status }) => {
  const color = status === "connected" ? "#10b981" : status === "connecting" ? "#f59e0b" : "#64748b";
  return <span style={{ display:"inline-block", width:7, height:7, borderRadius:"50%", background:color, flexShrink:0 }} />;
};

function AgentOverviewCard({ member }) {
  const isRecent = member.lastLoginAt && (Date.now() - new Date(member.lastLoginAt).getTime()) < 5 * 60 * 1000;
  const lastSeen = member.lastLoginAt
    ? (() => {
        const diff = Date.now() - new Date(member.lastLoginAt).getTime();
        if (diff < 60000)       return "Just now";
        if (diff < 3600000)     return `${Math.floor(diff/60000)}m ago`;
        if (diff < 86400000)    return `${Math.floor(diff/3600000)}h ago`;
        return `${Math.floor(diff/86400000)}d ago`;
      })()
    : "Never";

  return (
    <div style={{ background:T.bg1, border:`1px solid ${T.border1}`, borderRadius:16, padding:"16px 20px", display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ width:38, height:38, borderRadius:10, background:"rgba(99,102,241,0.12)", border:"1px solid rgba(99,102,241,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:700, color:"#6366f1", flexShrink:0 }}>
          {(member.name || member.email)[0].toUpperCase()}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.text1, display:"flex", alignItems:"center", gap:6 }}>
            {member.name || "—"}
            {isRecent && <span style={{ fontSize:9, padding:"1px 6px", borderRadius:5, background:"rgba(16,185,129,0.12)", color:"#10b981", fontWeight:700 }}>ONLINE</span>}
          </div>
          <div style={{ fontSize:11, color:T.text3 }}>{member.email}</div>
        </div>
        {!member.isActive && <span style={{ fontSize:10, padding:"2px 7px", borderRadius:6, background:"rgba(245,158,11,0.12)", color:"#f59e0b", fontWeight:600 }}>Pending</span>}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
        <div style={{ background:T.bg0, borderRadius:10, padding:"10px 12px" }}>
          <div style={{ fontSize:18, fontWeight:800, color:T.text1 }}>{member.msgsTodayCount ?? 0}</div>
          <div style={{ fontSize:10, color:T.text3, fontWeight:600 }}>Chats Today</div>
        </div>
        <div style={{ background:T.bg0, borderRadius:10, padding:"10px 12px" }}>
          <div style={{ fontSize:18, fontWeight:800, color:T.text1 }}>{member.totalChatsCount ?? 0}</div>
          <div style={{ fontSize:10, color:T.text3, fontWeight:600 }}>Total Chats</div>
        </div>
        <div style={{ background:T.bg0, borderRadius:10, padding:"10px 12px" }}>
          <div style={{ fontSize:12, fontWeight:700, color:T.text1, marginBottom:2 }}>{lastSeen}</div>
          <div style={{ fontSize:10, color:T.text3, fontWeight:600 }}>Last Active</div>
        </div>
      </div>

      {member.numbers?.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
          {member.numbers.map(n => (
            <div key={n.id} style={{ display:"flex", alignItems:"center", gap:5, padding:"3px 9px", borderRadius:7, background:T.bg2, border:`1px solid ${T.border2}` }}>
              <StatusDot status={n.status} />
              <span style={{ fontSize:11, color:T.text2 }}>{n.label}</span>
            </div>
          ))}
        </div>
      )}
      {(!member.numbers || member.numbers.length === 0) && member.isActive && (
        <div style={{ fontSize:11, color:T.text3 }}>No WhatsApp connected</div>
      )}
    </div>
  );
}

export default function TeamPage({ token, showToast }) {
  const [members,   setMembers]   = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [overview,  setOverview]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  // Invite form state
  const [invEmail,     setInvEmail]     = useState("");
  const [invName,      setInvName]      = useState("");
  const [invRole,      setInvRole]      = useState("agent");
  const [invPipelines, setInvPipelines] = useState([]);
  const [invAccess,    setInvAccess]    = useState(DEFAULT_ACCESS);
  const [inviting,     setInviting]     = useState(false);
  const [inviteLink,   setInviteLink]   = useState("");

  // Edit member state
  const [editingId,      setEditingId]      = useState(null);
  const [editRole,       setEditRole]       = useState("");
  const [editPipelines,  setEditPipelines]  = useState([]);
  const [editAccess,     setEditAccess]     = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [removingId,     setRemovingId]     = useState(null);
  const [resetMember,    setResetMember]    = useState(null);
  const [resetPassword,  setResetPassword]  = useState("");
  const [resetConfirm,   setResetConfirm]   = useState("");
  const [resetting,      setResetting]      = useState(false);

  const load = useCallback(async () => {
    try {
      const [teamData, plData, ovData] = await Promise.all([
        apiFetch("/api/team", {}, token),
        apiFetch("/api/pipelines", {}, token),
        apiFetch("/api/team/overview", {}, token),
      ]);
      setMembers(teamData.members || []);
      setPipelines(plData.pipelines || []);
      setOverview(ovData.overview || []);
    } catch { showToast?.("Failed to load team", "error"); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    if (!invEmail.trim()) { showToast?.("Email required", "error"); return; }
    setInviting(true); setInviteLink("");
    try {
      const d = await apiFetch("/api/team/invite", {
        method: "POST",
        body: JSON.stringify({ email: invEmail.trim(), name: invName.trim(), role: invRole, assignedPipelineIds: invPipelines, accessPages: invAccess }),
      }, token);
      if (d.error) { showToast?.(d.error, "error"); setInviting(false); return; }
      setInviteLink(d.inviteLink || "");
      showToast?.(`Invite sent to ${invEmail}`);
      await load();
      setInvEmail(""); setInvName(""); setInvRole("agent"); setInvPipelines([]); setInvAccess(DEFAULT_ACCESS);
    } catch { showToast?.("Failed to send invite", "error"); }
    setInviting(false);
  };

  const startEdit = (m) => {
    setEditingId(m._id);
    setEditRole(m.role);
    setEditPipelines(m.assignedPipelineIds?.map(String) || []);
    setEditAccess(m.accessPages?.length ? m.accessPages : (m.role === "admin" ? ADMIN_ACCESS : DEFAULT_ACCESS));
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const d = await apiFetch(`/api/team/${editingId}`, {
        method: "PATCH",
        body: JSON.stringify({ role: editRole, assignedPipelineIds: editPipelines, accessPages: editAccess }),
      }, token);
      if (d.error) { showToast?.(d.error, "error"); }
      else { showToast?.("Member updated"); await load(); }
    } catch { showToast?.("Failed to update", "error"); }
    setSaving(false); setEditingId(null);
  };

  const removeMember = async (id) => {
    try {
      await apiFetch(`/api/team/${id}`, { method: "DELETE" }, token);
      showToast?.("Member removed");
      setRemovingId(null);
      await load();
    } catch { showToast?.("Failed to remove", "error"); }
  };

  const openResetPassword = (member) => {
    setResetMember(member);
    setResetPassword("");
    setResetConfirm("");
  };

  const resetAgentPassword = async () => {
    if (!resetPassword || resetPassword.length < 8) { showToast?.("Password must be at least 8 characters", "error"); return; }
    if (!/[A-Z]/.test(resetPassword) || !/[0-9]/.test(resetPassword) || !/[^A-Za-z0-9]/.test(resetPassword)) {
      showToast?.("Password must include uppercase, number, and symbol", "error");
      return;
    }
    if (resetPassword !== resetConfirm) { showToast?.("Passwords do not match", "error"); return; }

    setResetting(true);
    try {
      const d = await apiFetch(`/api/team/${resetMember._id}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ password: resetPassword }),
      }, token);
      if (d.error) showToast?.(d.error, "error");
      else {
        showToast?.("Password reset successfully");
        setResetMember(null);
        await load();
      }
    } catch { showToast?.("Failed to reset password", "error"); }
    setResetting(false);
  };

  const togglePipeline = (id, arr, setArr) => {
    setArr(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  };

  const toggleAccess = (id, arr, setArr) => {
    if (["numbers", "profile"].includes(id)) return;
    setArr(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  };

  const AccessPicker = ({ value, setValue }) => (
    <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
      {ACCESS_GROUPS.map(group => (
        <div key={group.title}>
          <div style={{ fontSize:10,fontWeight:700,color:T.text3,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6 }}>{group.title}</div>
          <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
            {group.items.map(([id,label]) => {
              const locked = ["numbers", "profile"].includes(id);
              const active = value.includes(id);
              return (
                <button key={id} onClick={()=>toggleAccess(id,value,setValue)}
                  style={{ padding:"6px 10px",borderRadius:8,border:`1px solid ${active?"rgba(99,102,241,0.5)":T.border2}`,background:active?"rgba(99,102,241,0.1)":"transparent",color:active?"#6366f1":T.text2,cursor:locked?"default":"pointer",fontFamily:base.font,fontSize:11,fontWeight:600,opacity:locked?0.85:1 }}>
                  {active ? "✓ " : ""}{label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  const inputStyle = {
    width:"100%", boxSizing:"border-box", padding:"10px 13px",
    background:T.bg0, border:`1px solid ${T.border2}`, borderRadius:10,
    color:T.text1, fontSize:13, outline:"none", fontFamily:base.font,
  };

  const Label = ({ children }) => (
    <label style={{ display:"block", fontSize:11, fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>{children}</label>
  );

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"24px 28px", background:T.bg0 }}>
      <div style={{ maxWidth:780 }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:T.text1, marginBottom:4 }}>Team Members</div>
            <div style={{ fontSize:13, color:T.text3 }}>Invite agents/admins — they'll see only their assigned pipelines</div>
          </div>
          <button
            onClick={() => { setInviteOpen(true); setInviteLink(""); }}
            style={{ padding:"10px 18px", borderRadius:11, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", cursor:"pointer", fontFamily:base.font, fontSize:13, fontWeight:700, boxShadow:"0 4px 16px rgba(99,102,241,0.3)" }}
          >
            + Invite Member
          </button>
        </div>

        {/* Invite Modal */}
        {inviteOpen && (
          <div style={{ position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.72)",backdropFilter:"blur(5px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
            <div style={{ background:T.bg1,border:`1px solid ${T.border1}`,borderRadius:22,width:"100%",maxWidth:480,maxHeight:"90vh",overflow:"auto",boxShadow:"0 28px 80px rgba(0,0,0,0.6)" }}>
              <div style={{ padding:"22px 26px 18px",borderBottom:`1px solid ${T.border1}`,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <div style={{ fontSize:16,fontWeight:800,color:T.text1 }}>Invite Team Member</div>
                <button onClick={()=>{setInviteOpen(false);setInviteLink("");}} style={{ background:"none",border:"none",color:T.text3,cursor:"pointer",fontSize:22,lineHeight:1 }}>×</button>
              </div>
              <div style={{ padding:"20px 26px" }}>
                {inviteLink ? (
                  <div>
                    <div style={{ padding:"16px",borderRadius:12,background:"rgba(16,185,129,0.08)",border:"1px solid rgba(16,185,129,0.2)",marginBottom:16 }}>
                      <div style={{ fontSize:13,fontWeight:700,color:"#10b981",marginBottom:6 }}>✓ Invite sent! Share this link too:</div>
                      <div style={{ fontSize:11,color:T.text2,wordBreak:"break-all",background:T.bg0,padding:"8px 10px",borderRadius:8 }}>{inviteLink}</div>
                      <button onClick={()=>{navigator.clipboard?.writeText(inviteLink);showToast?.("Link copied");}} style={{ marginTop:8,padding:"6px 12px",borderRadius:8,border:`1px solid rgba(16,185,129,0.3)`,background:"rgba(16,185,129,0.1)",color:"#10b981",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:base.font }}>Copy Link</button>
                    </div>
                    <button onClick={()=>{setInviteLink("");}} style={{ width:"100%",padding:"10px",borderRadius:10,border:`1px solid ${T.border1}`,background:T.bg0,color:T.text2,cursor:"pointer",fontFamily:base.font,fontSize:13 }}>Invite Another</button>
                  </div>
                ) : (
                  <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
                    <div><Label>Email Address *</Label><input style={inputStyle} value={invEmail} onChange={e=>setInvEmail(e.target.value)} placeholder="agent@example.com" onFocus={e=>(e.target.style.borderColor="#6366f1")} onBlur={e=>(e.target.style.borderColor=T.border2)} /></div>
                    <div><Label>Name (optional)</Label><input style={inputStyle} value={invName} onChange={e=>setInvName(e.target.value)} placeholder="Ahmed Khan" onFocus={e=>(e.target.style.borderColor="#6366f1")} onBlur={e=>(e.target.style.borderColor=T.border2)} /></div>
                    <div>
                      <Label>Role</Label>
                      <div style={{ display:"flex",gap:8 }}>
                        {["agent","admin"].map(r => (
                          <button key={r} onClick={()=>{ setInvRole(r); setInvAccess(r === "admin" ? ADMIN_ACCESS : DEFAULT_ACCESS); }} style={{ flex:1,padding:"10px",borderRadius:10,border:`1px solid ${invRole===r?"rgba(99,102,241,0.5)":T.border2}`,background:invRole===r?"rgba(99,102,241,0.1)":"transparent",color:invRole===r?"#818cf8":T.text3,cursor:"pointer",fontFamily:base.font,fontSize:12,fontWeight:700,textTransform:"capitalize" }}>
                            {r}
                            <div style={{ fontSize:10,fontWeight:400,color:T.text3,marginTop:2 }}>{r==="agent"?"Sees assigned pipelines only":"Sees all pipelines"}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    {pipelines.length > 0 && (
                      <div>
                        <Label>Assign Pipelines (agent sees these only)</Label>
                        <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                          {pipelines.map(p => (
                            <button key={p._id} onClick={()=>togglePipeline(String(p._id),invPipelines,setInvPipelines)}
                              style={{ padding:"6px 12px",borderRadius:8,border:`1px solid ${invPipelines.includes(String(p._id))?"rgba(99,102,241,0.5)":T.border2}`,background:invPipelines.includes(String(p._id))?"rgba(99,102,241,0.1)":"transparent",color:invPipelines.includes(String(p._id))?"#818cf8":T.text2,cursor:"pointer",fontFamily:base.font,fontSize:12 }}>
                              {invPipelines.includes(String(p._id))?"✓ ":""}{p.name}
                            </button>
                          ))}
                        </div>
                        {invPipelines.length===0 && <div style={{ fontSize:11,color:T.text3,marginTop:4 }}>No pipelines selected = sees all pipelines (like admin)</div>}
                      </div>
                    )}
                    <div>
                      <Label>Module Access</Label>
                      <AccessPicker value={invAccess} setValue={setInvAccess} />
                    </div>
                    <div style={{ display:"flex",gap:10,marginTop:4 }}>
                      <button onClick={()=>setInviteOpen(false)} style={{ flex:1,padding:"10px",borderRadius:10,border:`1px solid ${T.border1}`,background:T.bg0,color:T.text2,cursor:"pointer",fontFamily:base.font,fontSize:13 }}>Cancel</button>
                      <button onClick={sendInvite} disabled={inviting} style={{ flex:2,padding:"10px",borderRadius:10,border:"none",background:inviting?T.bg2:"linear-gradient(135deg,#6366f1,#4f46e5)",color:inviting?T.text3:"#fff",cursor:inviting?"not-allowed":"pointer",fontFamily:base.font,fontSize:13,fontWeight:700,boxShadow:inviting?"none":"0 4px 16px rgba(99,102,241,0.3)" }}>
                        {inviting?"Sending...":"Send Invite"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Remove confirm */}
        {removingId && (
          <div style={{ position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
            <div style={{ background:T.bg1,border:`1px solid ${T.border1}`,borderRadius:18,padding:"26px",width:"100%",maxWidth:340,boxShadow:"0 24px 60px rgba(0,0,0,0.6)" }}>
              <div style={{ fontSize:15,fontWeight:800,color:"#f43f5e",marginBottom:8 }}>Remove Member?</div>
              <div style={{ fontSize:13,color:T.text2,marginBottom:20 }}>They'll lose access immediately.</div>
              <div style={{ display:"flex",gap:10 }}>
                <button onClick={()=>setRemovingId(null)} style={{ flex:1,padding:"10px",borderRadius:10,border:`1px solid ${T.border1}`,background:T.bg0,color:T.text2,cursor:"pointer",fontFamily:base.font,fontSize:13,fontWeight:600 }}>Cancel</button>
                <button onClick={()=>removeMember(removingId)} style={{ flex:1,padding:"10px",borderRadius:10,border:"none",background:"#f43f5e",color:"#fff",cursor:"pointer",fontFamily:base.font,fontSize:13,fontWeight:700 }}>Remove</button>
              </div>
            </div>
          </div>
        )}

        {/* Reset password */}
        {resetMember && (
          <div style={{ position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
            <div style={{ background:T.bg1,border:`1px solid ${T.border1}`,borderRadius:18,padding:"26px",width:"100%",maxWidth:380,boxShadow:"0 24px 60px rgba(0,0,0,0.6)" }}>
              <div style={{ fontSize:15,fontWeight:800,color:T.text1,marginBottom:6 }}>Reset Password</div>
              <div style={{ fontSize:12,color:T.text3,marginBottom:18 }}>{resetMember.email}</div>
              <div style={{ marginBottom:12 }}>
                <Label>New Password</Label>
                <input type="password" value={resetPassword} onChange={e=>setResetPassword(e.target.value)} placeholder="Min 8 chars, uppercase, number, symbol" style={inputStyle} onFocus={e=>(e.target.style.borderColor="#6366f1")} onBlur={e=>(e.target.style.borderColor=T.border2)} />
              </div>
              <div style={{ marginBottom:18 }}>
                <Label>Confirm Password</Label>
                <input type="password" value={resetConfirm} onChange={e=>setResetConfirm(e.target.value)} placeholder="Re-enter new password" style={inputStyle} onKeyDown={e=>e.key==="Enter"&&resetAgentPassword()} onFocus={e=>(e.target.style.borderColor="#6366f1")} onBlur={e=>(e.target.style.borderColor=T.border2)} />
              </div>
              <div style={{ display:"flex",gap:10 }}>
                <button onClick={()=>setResetMember(null)} style={{ flex:1,padding:"10px",borderRadius:10,border:`1px solid ${T.border1}`,background:T.bg0,color:T.text2,cursor:"pointer",fontFamily:base.font,fontSize:13,fontWeight:600 }}>Cancel</button>
                <button onClick={resetAgentPassword} disabled={resetting} style={{ flex:1.4,padding:"10px",borderRadius:10,border:"none",background:resetting?T.bg2:"linear-gradient(135deg,#6366f1,#4f46e5)",color:resetting?T.text3:"#fff",cursor:resetting?"not-allowed":"pointer",fontFamily:base.font,fontSize:13,fontWeight:700 }}>{resetting?"Saving...":"Set Password"}</button>
              </div>
            </div>
          </div>
        )}

        {/* Agent Overview */}
        {!loading && overview.filter(m => m.isActive).length > 0 && (
          <div style={{ marginBottom:28 }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:12 }}>Live Activity</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12 }}>
              {overview.filter(m => m.isActive).map(m => (
                <AgentOverviewCard key={m._id} member={m} />
              ))}
            </div>
          </div>
        )}

        {/* Members table */}
        {loading ? (
          <div style={{ textAlign:"center",padding:60,color:T.text3 }}>Loading team...</div>
        ) : members.length === 0 ? (
          <div style={{ background:T.bg1,border:`1px solid ${T.border1}`,borderRadius:18,padding:"48px 24px",textAlign:"center" }}>
            <div style={{ fontSize:32,marginBottom:12 }}>👥</div>
            <div style={{ fontSize:16,fontWeight:700,color:T.text1,marginBottom:6 }}>No team members yet</div>
            <div style={{ fontSize:13,color:T.text3 }}>Invite agents or admins to collaborate on leads</div>
          </div>
        ) : (
          <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
            {members.map(m => (
              <div key={m._id} style={{ background:T.bg1,border:`1px solid ${T.border1}`,borderRadius:16,padding:"16px 20px" }}>
                {editingId === m._id ? (
                  /* Edit mode */
                  <div>
                    <div style={{ display:"flex",gap:12,alignItems:"flex-start",marginBottom:14 }}>
                      <Avatar name={m.name||m.email} />
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14,fontWeight:700,color:T.text1 }}>{m.name||"—"}</div>
                        <div style={{ fontSize:12,color:T.text3 }}>{m.email}</div>
                      </div>
                    </div>
                    <div style={{ marginBottom:12 }}>
                      <Label>Role</Label>
                      <div style={{ display:"flex",gap:6 }}>
                        {["agent","admin"].map(r => (
                          <button key={r} onClick={()=>{ setEditRole(r); setEditAccess(r === "admin" ? ADMIN_ACCESS : DEFAULT_ACCESS); }} style={{ padding:"7px 14px",borderRadius:9,border:`1px solid ${editRole===r?"rgba(99,102,241,0.5)":T.border2}`,background:editRole===r?"rgba(99,102,241,0.1)":"transparent",color:editRole===r?"#818cf8":T.text3,cursor:"pointer",fontFamily:base.font,fontSize:12,fontWeight:600,textTransform:"capitalize" }}>{r}</button>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginBottom:14 }}>
                      <Label>Assigned Pipelines</Label>
                      <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                        {pipelines.map(p => (
                          <button key={p._id} onClick={()=>togglePipeline(String(p._id),editPipelines,setEditPipelines)}
                            style={{ padding:"5px 11px",borderRadius:8,border:`1px solid ${editPipelines.includes(String(p._id))?"rgba(99,102,241,0.5)":T.border2}`,background:editPipelines.includes(String(p._id))?"rgba(99,102,241,0.1)":"transparent",color:editPipelines.includes(String(p._id))?"#818cf8":T.text2,cursor:"pointer",fontFamily:base.font,fontSize:11 }}>
                            {editPipelines.includes(String(p._id))?"✓ ":""}{p.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginBottom:14 }}>
                      <Label>Module Access</Label>
                      <AccessPicker value={editAccess} setValue={setEditAccess} />
                    </div>
                    <div style={{ display:"flex",gap:8 }}>
                      <button onClick={()=>setEditingId(null)} style={{ padding:"8px 14px",borderRadius:9,border:`1px solid ${T.border1}`,background:T.bg0,color:T.text2,cursor:"pointer",fontFamily:base.font,fontSize:12 }}>Cancel</button>
                      <button onClick={saveEdit} disabled={saving} style={{ padding:"8px 16px",borderRadius:9,border:"none",background:saving?T.bg2:"linear-gradient(135deg,#6366f1,#4f46e5)",color:saving?T.text3:"#fff",cursor:saving?"not-allowed":"pointer",fontFamily:base.font,fontSize:12,fontWeight:700 }}>{saving?"Saving...":"Save Changes"}</button>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                    <Avatar name={m.name||m.email} />
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:3 }}>
                        <span style={{ fontSize:14,fontWeight:700,color:T.text1 }}>{m.name||"—"}</span>
                        <RoleBadge role={m.role} />
                        {!m.isActive && <span style={{ fontSize:10,padding:"1px 7px",borderRadius:6,background:"rgba(245,158,11,0.12)",color:"#f59e0b",fontWeight:600 }}>Pending invite</span>}
                      </div>
                      <div style={{ fontSize:12,color:T.text3,marginBottom:4 }}>{m.email}</div>
                      {m.assignedPipelineIds?.length > 0 && (
                        <div style={{ display:"flex",flexWrap:"wrap",gap:4 }}>
                          {m.assignedPipelineIds.map(pid => {
                            const pl = pipelines.find(p=>String(p._id)===String(pid));
                            return pl ? <span key={pid} style={{ fontSize:10,padding:"1px 7px",borderRadius:6,background:"rgba(99,102,241,0.1)",color:"#818cf8" }}>{pl.name}</span> : null;
                          })}
                        </div>
                      )}
                      {(!m.assignedPipelineIds||m.assignedPipelineIds.length===0) && (
                        <div style={{ fontSize:11,color:T.text3 }}>All pipelines</div>
                      )}
                      {m.accessPages?.length > 0 && (
                        <div style={{ fontSize:11,color:T.text3,marginTop:4 }}>{m.accessPages.length} modules allowed</div>
                      )}
                    </div>
                    <div style={{ display:"flex",gap:6,flexShrink:0 }}>
                      <button onClick={()=>startEdit(m)} style={{ padding:"7px 13px",borderRadius:9,border:`1px solid ${T.border1}`,background:T.bg0,color:T.text2,cursor:"pointer",fontFamily:base.font,fontSize:12,fontWeight:600 }}>Edit</button>
                      <button onClick={()=>openResetPassword(m)} style={{ padding:"7px 13px",borderRadius:9,border:`1px solid ${T.border1}`,background:T.bg0,color:"#6366f1",cursor:"pointer",fontFamily:base.font,fontSize:12,fontWeight:600 }}>Reset Password</button>
                      <button onClick={()=>setRemovingId(m._id)} style={{ padding:"7px 13px",borderRadius:9,border:"1px solid rgba(244,63,94,0.3)",background:"rgba(244,63,94,0.06)",color:"#f43f5e",cursor:"pointer",fontFamily:base.font,fontSize:12,fontWeight:600 }}>Remove</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
